import axios from "axios";
import { toast } from "react-toastify";
import { store } from "../redux/Store";
// import { logout } from "../redux/AuthSlice";
import { BASE_URL } from "./Endpoints";

const API_BASE_URL = BASE_URL


/* PUBLIC ROUTES */
const PUBLIC_ROUTES = [
  "/auth/login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/resend-otp",
  "/auth/verify-otp",
  "/auth/reset-password",
  "/auth/refresh",
];

const isPublicRoute = (url = "") => {
  const cleanUrl = url.split("?")[0];
  return PUBLIC_ROUTES.some((route) => cleanUrl.endsWith(route));
};

/* AXIOS INSTANCE */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

/* REQUEST INTERCEPTOR */
apiClient.interceptors.request.use(
  (config) => {
    /* Remove content type for FormData */
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    /* Attach token */
    const token = store.getState()?.auth?.access_token;

    if (
      token &&
      !isPublicRoute(config.url) &&
      !config.headers?.Authorization
    ) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* RESPONSE INTERCEPTOR */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url || "";

    /* Unauthorized */
    if (status === 401) {
      if (isPublicRoute(requestUrl)) {
        toast.error("Invalid credentials");
      } else {
        toast.error("Session expired. Please login again.");

        // store.dispatch(logout());

        localStorage.clear();

        window.location.href = "/";
      }
    }

    /* Forbidden */
    else if (status === 403) {
      toast.warning("You are not authorized");
    }

    /* Validation */
    else if (status === 422) {
      const data = error?.response?.data;

      let message = "Validation error";

      if (Array.isArray(data?.errors)) {
        message = data.errors
          .map((item) =>
            typeof item === "string"
              ? item
              : item?.error || `Row ${item?.row}`
          )
          .join(", ");
      } else {
        message =
          data?.message ||
          data?.error ||
          "Validation error occurred";
      }

      toast.error(message);
    }

    /* Server Error */
    else if (status >= 500) {
      toast.error("Server error. Please try again later.");
    }

    /* General Error */
    else {
      toast.error(
        error?.response?.data?.message ||
        error.message ||
        "Something went wrong"
      );
    }

    return Promise.reject(error);
  }
);

/* COMMON REQUEST HANDLER */
const requestHandler = async (
  request,
  {
    showSuccessToast = false,
    successMessage = "",
    useApiSuccessMessage = true,
  } = {}
) => {
  try {
    const response = await request();

    if (showSuccessToast) {
      const apiMessage = response?.data?.message;

      toast.success(
        successMessage ||
        (useApiSuccessMessage
          ? apiMessage
          : "Operation successful")
      );
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

/* METHODS */
export const apiGet = (url, config = {}, options = {}) =>
  requestHandler(() => apiClient.get(url, config), options);

export const apiPost = (url, data = {}, config = {}, options = {}) =>
  requestHandler(() => apiClient.post(url, data, config), options);

export const apiPut = (url, data = {}, config = {}, options = {}) =>
  requestHandler(() => apiClient.put(url, data, config), options);

export const apiPatch = (url, data = {}, config = {}, options = {}) =>
  requestHandler(() => apiClient.patch(url, data, config), options);

export const apiDelete = (url, config = {}, options = {}) =>
  requestHandler(() => apiClient.delete(url, config), options);

export default apiClient;